import capitalize from 'lodash/capitalize';
import { useEffect } from 'react';

import { queryClient } from 'config/queryClient';
import { PageContext } from 'hooks/shared/usePageContext.types';
import { CANDIDATE_STATUS } from 'services/candidates';
import { EventBus } from 'services/event-bus/EventBus';
import {
  CandidateEvent,
  EventBusCandidateEventTransactionItem,
  EventBusEvent,
  EventBusPayload,
  EventBusTransactionRecord,
} from 'services/event-bus/EventBus.types';
import { CampaignTab } from 'shared/legacy/contexts/SelectedCampaignContext/SelectedCampaignContext.types';
import { useCampaignStore } from 'stores/campaign/campaign.store';
import { useLocalizationStore } from 'stores/localization/localization.store';
import { useSnackbarStore } from 'stores/snackbar/snackbar.store';
import { QueryKey } from 'types/query';
import { calculateCandidateStatusFromLabels } from 'utils/candidates/calculateCandidateStatusFromLabels';
import { getPageContext } from 'utils/getPageContext';
import {
  decreaseJobboardCount,
  decreaseStatCount,
  increaseStatCount,
  resetList,
  updateCandidateAtSharedAndCandidate,
  updateCandidateQueries,
} from './updates';

const onUnlocked = (payload: EventBusPayload<CandidateEvent.Unlocked>) => {
  const pageContext = getPageContext();

  if (pageContext === PageContext.Campaign || pageContext === PageContext.RatedCandidates) {
    const queryKeysToUpdate = [QueryKey.useRatedCandidatesQuery, QueryKey.selectedCampaignContextUseCandidates];
    updateCandidateQueries(queryKeysToUpdate, payload.esPersonId, { is_unlocked: true });

    return;
  }

  if (pageContext === PageContext.Shared || pageContext === PageContext.Candidate) {
    updateCandidateAtSharedAndCandidate(payload.esPersonId, { is_unlocked: true });
  }
};

const onLabelsUpdated = (payload: EventBusPayload<CandidateEvent.LabelsUpdated>) => {
  const pageContext = getPageContext();

  if (pageContext === PageContext.Campaign || pageContext === PageContext.RatedCandidates) {
    const queryKeysToUpdate = [QueryKey.useRatedCandidatesQuery, QueryKey.selectedCampaignContextUseCandidates];

    updateCandidateQueries(queryKeysToUpdate, payload.esPersonId, { reasons: payload.labels });

    return;
  }

  if (pageContext === PageContext.Shared || pageContext === PageContext.Candidate) {
    updateCandidateAtSharedAndCandidate(payload.esPersonId, { reasons: payload.labels });
  }
};

const onContacted = (payload: EventBusPayload<CandidateEvent.Contacted>, transactionKey?: string) => {
  const history = EventBus.getTransactionHistory<CandidateEvent>(transactionKey || payload.esPersonId);

  let currentLabels: string[] = [];

  if (history) {
    const expectedEvents = getCandidateExpectedEvents(
      [CandidateEvent.StartTransaction, CandidateEvent.LabelsUpdated],
      history,
    );

    const startTransaction = expectedEvents[CandidateEvent.StartTransaction];
    const labelsUpdated = expectedEvents[CandidateEvent.LabelsUpdated];

    currentLabels = labelsUpdated?.labels ?? startTransaction?.labels ?? [];
  }

  if (!currentLabels.includes('contacted')) {
    const activeTransactionKey = transactionKey || payload.esPersonId;

    EventBus.emit(
      CandidateEvent.LabelsUpdated,
      { labels: [...currentLabels, 'contacted'], esPersonId: payload.esPersonId },
      activeTransactionKey,
    );
  }
};

const onCommitTransaction = (payload: EventBusPayload<CandidateEvent.CommitTransaction>) => {
  const createSnackbar = useSnackbarStore.getState().createSnackbar;
  const dictionary = useLocalizationStore.getState().dictionary;

  const expectedEvents = getCandidateExpectedEvents(
    [CandidateEvent.StartTransaction, CandidateEvent.LabelsUpdated, CandidateEvent.Contacted],
    payload.history,
  );
  const pageContext = getPageContext();
  const currentTab = useCampaignStore.getState().selectedTab;

  const startTransaction = expectedEvents[CandidateEvent.StartTransaction];
  const labelsUpdated = expectedEvents[CandidateEvent.LabelsUpdated];
  const contacted = expectedEvents[CandidateEvent.Contacted];

  if (!startTransaction) {
    throw Error('You need to start a transaction before committing it');
  }

  const name = startTransaction.name;
  const esPersonId = startTransaction.esPersonId;
  const initialStatus = startTransaction.status;
  const initialLabels = startTransaction.labels;
  const finalLabels = labelsUpdated?.labels ?? initialLabels;

  const { newStatus } = calculateCandidateStatusFromLabels({
    currentStatus: initialStatus,
    currentLabels: initialLabels,
    newLabels: finalLabels,
    fallbackStatus: contacted && CANDIDATE_STATUS.CONTACTED,
  });

  if (
    contacted &&
    [CANDIDATE_STATUS.CONTACTED, CANDIDATE_STATUS.MATCHED].every((s) => s !== initialStatus) &&
    [PageContext.Campaign, PageContext.RatedCandidates].includes(pageContext)
  ) {
    switch (initialStatus) {
      case CANDIDATE_STATUS.NEW:
        decreaseStatCount(CampaignTab.New);
        break;
      case CANDIDATE_STATUS.SHORTLISTED:
        decreaseStatCount(CampaignTab.Shortlisted);
        break;
      case CANDIDATE_STATUS.HIDDEN:
        decreaseStatCount(CampaignTab.Hidden);
        break;
    }

    if (pageContext === PageContext.Campaign && initialStatus === CANDIDATE_STATUS.NEW) {
      decreaseJobboardCount(contacted.activeJobBoard);
    }
  }

  if (
    initialStatus === CANDIDATE_STATUS.CONTACTED &&
    newStatus !== CANDIDATE_STATUS.CONTACTED &&
    [PageContext.Campaign, PageContext.RatedCandidates].includes(pageContext)
  ) {
    decreaseStatCount(CampaignTab.Contacted);
  }

  if (pageContext === PageContext.Campaign || pageContext === PageContext.RatedCandidates) {
    const queryKeysToUpdate = [QueryKey.useRatedCandidatesQuery, QueryKey.selectedCampaignContextUseCandidates];

    updateCandidateQueries(queryKeysToUpdate, esPersonId, { status: newStatus });

    if (newStatus !== initialStatus) {
      updateCandidateQueries(queryKeysToUpdate, esPersonId, {}, { remove: true });
    }

    switch (newStatus) {
      case CANDIDATE_STATUS.CONTACTED:
        if (initialStatus === CANDIDATE_STATUS.CONTACTED) break;

        increaseStatCount(CampaignTab.Contacted);
        resetList(CampaignTab.Contacted);
        break;
      case CANDIDATE_STATUS.MATCHED:
        if (initialStatus === CANDIDATE_STATUS.MATCHED) break;

        increaseStatCount(CampaignTab.Matched);
        resetList(CampaignTab.Matched);
        break;
      case CANDIDATE_STATUS.HIDDEN:
        if (initialStatus === CANDIDATE_STATUS.HIDDEN) break;

        increaseStatCount(CampaignTab.Hidden);
        break;
    }

    if (currentTab !== newStatus && newStatus !== CANDIDATE_STATUS.INVISIBLE) {
      resetList(newStatus as CampaignTab);
    }
  } else if (pageContext === PageContext.Shared || pageContext === PageContext.Candidate) {
    updateCandidateAtSharedAndCandidate(esPersonId, { status: newStatus });
  }

  queryClient.invalidateQueries([QueryKey.useCandidateCampaignQuery, { esPersonId }], {
    exact: false,
  });
  queryClient.invalidateQueries([QueryKey.useCandidateActivityLogQuery, { candidateEsPersonId: esPersonId }], {
    exact: false,
  });
  queryClient.invalidateQueries([QueryKey.useCandidateHistoryQuery, { candidateEsPersonId: esPersonId }], {
    exact: false,
  });
  queryClient.invalidateQueries([QueryKey.useTenantDistinctedLabelsQuery], { exact: false });

  if (initialStatus !== newStatus) {
    createSnackbar(dictionary.candidateIsMovedTo(name, capitalize(newStatus)), { variant: 'success' });
  }
};

export const useCandidateCacheUpdater = () => {
  useEffect(() => {
    const disposables: (() => void)[] = [];

    disposables.push(EventBus.on(CandidateEvent.Contacted, onContacted));
    disposables.push(EventBus.on(CandidateEvent.Unlocked, onUnlocked));
    disposables.push(EventBus.on(CandidateEvent.LabelsUpdated, onLabelsUpdated));
    disposables.push(EventBus.on(CandidateEvent.CommitTransaction, onCommitTransaction));

    return () => disposables.forEach((disposable) => disposable());
  }, []);
};

const getCandidateExpectedEvents = (
  expectedEventKeys: EventBusEvent[],
  history: EventBusTransactionRecord<EventBusEvent, EventBusPayload<EventBusEvent>>[],
) => {
  const expectedEvents: Partial<{ [K in CandidateEvent]: EventBusTransactionRecord<K>['payload'] }> = {};

  history.forEach((h) => {
    if (isItemCandidateEvent(h) && expectedEventKeys.includes(h.event) && !expectedEvents[h.event]) {
      // Since we are using type guards, and TypeScript cannot map a runtime value to a mapped type value
      // cast as any is acceptable
      expectedEvents[h.event] = h.payload as any;
    }
  });

  return expectedEvents;
};

const isCandidateEvent = (event: EventBusEvent): event is CandidateEvent => {
  return Object.values(CandidateEvent).includes(event as any);
};

const isItemCandidateEvent = (
  historyItem: EventBusTransactionRecord<EventBusEvent>,
): historyItem is EventBusCandidateEventTransactionItem => {
  return isCandidateEvent(historyItem.event);
};
